import { Component } from '@theme/component';
import { DialogComponent, DialogOpenEvent } from '@theme/dialog';

/**
 * Browse Categories panel — desktop split-pane modal + mobile exclusive accordion.
 *
 * @typedef {object} Refs
 * @property {HTMLInputElement} [search]
 * @property {HTMLElement} [empty]
 *
 * @extends {Component<Refs>}
 */
class CategoryBrowser extends Component {
  /** @type {AbortController | null} */
  #abort = null;

  /** @type {DialogComponent | null} */
  #dialogHost = null;

  connectedCallback() {
    super.connectedCallback();

    this.#abort = new AbortController();
    const { signal } = this.#abort;

    if (this.#isDesktop) {
      document.addEventListener('click', this.#onTriggerClick, { signal });
      const host = this.closest('dialog-component');
      this.#dialogHost = host instanceof DialogComponent ? host : null;
      this.#dialogHost?.addEventListener(DialogOpenEvent.eventName, this.#onDialogOpen, { signal });
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abort?.abort();
    this.#abort = null;
    this.#dialogHost = null;
  }

  get #isDesktop() {
    return this.dataset.variant === 'desktop';
  }

  get #triggerId() {
    return this.dataset.triggerId ?? '';
  }

  /**
   * @param {Element | null} el
   */
  #isBrowseCollectionsControl(el) {
    if (!(el instanceof HTMLElement)) return false;
    if (el.matches('input, textarea, select, option')) return false;
    const text = (el.textContent ?? '').replace(/\s+/g, ' ').trim().toLowerCase();
    return text === 'browse collections';
  }

  /**
   * @param {Element | null} trigger
   * @returns {trigger is HTMLElement}
   */
  #isOwnTrigger(trigger) {
    if (!(trigger instanceof HTMLElement)) return false;
    if (trigger.closest('category-browser')) return false;

    const id = trigger.getAttribute('data-open-category-browser') ?? '';
    if (id && this.#triggerId && id === this.#triggerId) return true;

    const hasAttr = trigger.hasAttribute('data-open-category-browser');
    const isGenericAttr = hasAttr && (!id || id === '*' || id === 'all');
    if (!isGenericAttr && !this.#isBrowseCollectionsControl(trigger)) return false;

    const first = document.querySelector('category-browser.category-browser--desktop');
    return first === this;
  }

  /**
   * Header Category click, or any Browse Collections control, opens the modal.
   * @param {MouseEvent} event
   */
  #onTriggerClick = (event) => {
    if (event.defaultPrevented) return;
    if (event.button !== 0) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

    const target = event.target;
    if (!(target instanceof Element)) return;

    let trigger = target.closest('[data-open-category-browser]');
    if (!trigger) {
      const clickable = target.closest('a, button');
      if (this.#isBrowseCollectionsControl(clickable)) trigger = clickable;
    }
    if (!this.#isOwnTrigger(trigger)) return;

    event.preventDefault();
    this.open();
  };

  #onDialogOpen = () => {
    requestAnimationFrame(() => {
      const dialog = this.#dialogHost?.refs?.dialog;
      if (dialog instanceof HTMLDialogElement && dialog.open) {
        dialog.focus({ preventScroll: true });
      }
    });
  };

  open() {
    const dialog = this.#dialogHost?.refs?.dialog;
    if (dialog instanceof HTMLDialogElement && dialog.open) return;

    if (this.refs.search) this.refs.search.value = '';
    this.filter();
    this.#select();
    this.#dialogHost?.showDialog();
  }

  /**
   * Select a main category by handle. Used by click/hover handlers.
   * @param {Event | string} [eventOrHandle]
   */
  select = (eventOrHandle) => {
    let handle = '';

    if (typeof eventOrHandle === 'string') {
      handle = eventOrHandle;
    } else if (eventOrHandle instanceof Event) {
      const target = eventOrHandle.target;
      if (target instanceof Element) {
        const source = target.closest('[data-category-handle]');
        handle = source?.getAttribute('data-category-handle') ?? '';
      }
    }

    this.#select(handle);
  };

  /**
   * @param {string} [handle]
   */
  #select(handle) {
    const mains = this.querySelectorAll('[data-category-main]');
    const panels = this.querySelectorAll('[data-category-panel]');
    if (!mains.length) return;

    const visibleMains = [...mains].filter((el) => el instanceof HTMLElement && !el.hidden);
    if (!visibleMains.length) return;

    const requestedVisible = handle
      ? visibleMains.find((el) => el.getAttribute('data-category-handle') === handle)
      : null;
    const active = requestedVisible?.getAttribute('data-category-handle') ?? visibleMains[0]?.getAttribute('data-category-handle') ?? '';

    for (const main of mains) {
      const isActive = main.getAttribute('data-category-handle') === active && !/** @type {HTMLElement} */ (main).hidden;
      main.classList.toggle('is-active', isActive);
      const button = main.querySelector('[data-category-trigger]');
      if (button instanceof HTMLElement) {
        button.setAttribute('aria-selected', isActive ? 'true' : 'false');
        button.classList.toggle('is-active', isActive);
      }
    }

    for (const panel of panels) {
      const match = panel.getAttribute('data-category-panel') === active;
      panel.classList.toggle('is-current', match);
    }
  }

  /**
   * Exclusive accordion: opening one main category closes the others.
   * @param {Event} event
   */
  onAccordionToggle = (event) => {
    const details = event.target;
    if (!(details instanceof HTMLDetailsElement) || !details.open) return;

    this.querySelectorAll('details[data-category-accordion][open]').forEach((other) => {
      if (other !== details) other.open = false;
    });
  };

  /**
   * Keep Enter from submitting anything while filtering categories.
   * @param {KeyboardEvent} event
   */
  onSearchKeyDown = (event) => {
    if (event.key === 'Enter') event.preventDefault();
  };

  /**
   * Filter main and sub items by the search query.
   * @param {Event} [_event]
   */
  filter = (_event) => {
    const query = (this.refs.search?.value ?? '').trim().toLowerCase();
    const groups = this.querySelectorAll('[data-category-group]');
    let visibleCount = 0;
    let firstVisibleHandle = '';

    for (const group of groups) {
      if (!(group instanceof HTMLElement)) continue;

      const handle = group.getAttribute('data-category-handle') ?? '';
      const panel = handle
        ? this.querySelector(`[data-category-panel="${CSS.escape(handle)}"]`)
        : null;
      const mainTitle = (group.getAttribute('data-search') ?? '').toLowerCase();
      const mainMatch = !query || mainTitle.includes(query);
      const subScope = panel instanceof HTMLElement ? panel : group;
      const subs = subScope.querySelectorAll('[data-category-sub]');
      let visibleSubs = 0;

      for (const sub of subs) {
        if (!(sub instanceof HTMLElement)) continue;
        const subTitle = (sub.getAttribute('data-search') ?? '').toLowerCase();
        const subMatch = !query || subTitle.includes(query) || mainMatch;
        sub.hidden = !subMatch;
        if (!query || subTitle.includes(query) || mainMatch) visibleSubs += 1;
      }

      const showGroup = !query || mainMatch || visibleSubs > 0;
      group.hidden = !showGroup;
      if (panel instanceof HTMLElement) panel.hidden = !showGroup;

      if (showGroup) {
        visibleCount += 1;
        if (!firstVisibleHandle && handle) {
          firstVisibleHandle = handle;
        }
      }
    }

    if (this.refs.empty) {
      this.refs.empty.hidden = !(query && visibleCount === 0);
    }

    const body = this.querySelector('[data-category-body]');
    if (body instanceof HTMLElement) {
      body.hidden = visibleCount === 0;
    }

    if (this.#isDesktop) {
      const current =
        this.querySelector('[data-category-trigger].is-active')?.getAttribute('data-category-handle') ?? '';
      this.#select(current || firstVisibleHandle);
    } else if (query) {
      this.#openFirstVisibleAccordion(firstVisibleHandle);
    } else {
      this.querySelectorAll('details[data-category-accordion][open]').forEach((details) => {
        details.open = false;
      });
    }
  };

  /**
   * @param {string} handle
   */
  #openFirstVisibleAccordion(handle) {
    this.querySelectorAll('details[data-category-accordion]').forEach((details) => {
      if (!(details instanceof HTMLDetailsElement)) return;
      details.open = details.getAttribute('data-category-handle') === handle;
    });
  }
}

if (!customElements.get('category-browser')) {
  customElements.define('category-browser', CategoryBrowser);
}
